import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { openConstructionNotice } from '../utils/constructionNotice';
import Modal from './common/Modal';

const FooterWrapper = styled.footer`
  width: 100%;
  padding: 5rem 2rem 7rem;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  color: #555;
  font-size: 0.9rem;
  position: relative;

  @media (max-width: 768px) {
    padding: 3.5rem 1.25rem 5rem;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  /* Three tracks for three columns (brand + platform + resources). The fourth
     track was left over from a link column that no longer renders, so every
     page footer ended with a quarter of its width blank on the right
     (2026-08-14 pricing layout pass). */
  grid-template-columns: 2fr 1fr 1fr;
  gap: 2rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 3rem;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;

  &:first-child {
    grid-column: 1;
  }
`;

const FooterLogo = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: #222;
  margin-bottom: 1.2rem;
  letter-spacing: -0.03em;
  display: flex;
  align-items: center;
  gap: clamp(0.5rem, 1.5vw, 0.75rem);
  background: transparent;

  &::after {
    content: '';
    display: inline-block;
    width: 24px;
    height: 2px;
    background: linear-gradient(135deg, var(--brand-gold-alt), var(--brand-black));
    margin-left: 0.6rem;
    border-radius: 2px;
  }
`;

const FooterLogoImage = styled.img`
  width: clamp(36px, 6vw, 44px);
  height: clamp(36px, 6vw, 44px);
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(200, 167, 94, 0.15);
  background: linear-gradient(135deg, rgba(200, 167, 94, 0.08), rgba(26, 26, 26, 0.08));
`;

const FooterLogoText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0;
`;

const FooterInlineLogo = styled.img`
  display: inline-block;
  height: 1.1em;
  margin-left: -0.12em;
  margin-bottom: 0.18em;
  vertical-align: baseline;
  object-fit: contain;
  filter: drop-shadow(0 2px 6px rgba(200, 167, 94, 0.15));
`;

const FooterDescription = styled.p`
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  max-width: 400px;
  text-transform: lowercase;

  /* preserve the AI acronym in uppercase despite the global lowercase rule */
  .ai-uppercase {
    text-transform: lowercase;
  }
`;

const FooterTitle = styled.h4`
  font-weight: 600;
  color: #333;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  text-transform: lowercase;

  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 30px;
    height: 2px;
    background: linear-gradient(135deg, var(--brand-gold-alt), var(--brand-black));
    border-radius: 2px;
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 0.8rem;

  a {
    color: #666;
    text-decoration: none;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    padding: 0.25rem 0;
    text-transform: lowercase;

    &:hover {
      color: var(--brand-gold-alt);
      transform: translateX(3px);
    }

    svg {
      width: 16px;
      height: 16px;
      margin-right: 0.5rem;
      opacity: 0.7;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialIcon = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    background: var(--brand-gold-alt);
    color: white;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FooterBottom = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 2rem auto 0;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
    margin-bottom: 0;
  }
`;

const Copyright = styled.div`
  color: #777;
  font-size: 0.875rem;
  text-transform: none; /* Override inherited lowercase from parent containers */
`;

const FooterBottomLinks = styled.div`
  display: flex;
  gap: 1.5rem;

  a {
    color: #666;
    text-decoration: none;
    font-size: 0.875rem;
    transition: color 0.2s ease;
    text-transform: lowercase;
    min-height: 44px;
    display: inline-flex;
    align-items: center;

    &:hover {
      color: var(--brand-gold-alt);
    }
  }

  @media (max-width: 768px) {
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
  }
`;

// Demo video modal (shared across all pages via GlobalFooter)
const fadeInModal = keyframes`from { opacity: 0; } to { opacity: 1; }`;

const DemoModalOverlay = styled.div<{ $isOpen: boolean; $mobileFs?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.88);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  padding: 2rem;
  cursor: pointer;
  animation: ${fadeInModal} 0.2s ease-out;

  @media (max-width: 768px) {
    padding: ${({ $mobileFs }) => $mobileFs ? '0' : '1rem'};
    background: ${({ $mobileFs }) => $mobileFs ? '#000' : 'rgba(0, 0, 0, 0.88)'};
  }
`;

const DemoModalContent = styled.div<{ $mobileFs?: boolean }>`
  position: relative;
  width: 100%;
  max-width: 1100px;
  cursor: default;

  @media (max-width: 768px) {
    ${({ $mobileFs }) => $mobileFs ? `
      max-width: 100%;
      width: 100vw;
      height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
    ` : ''}
  }
`;

const DemoModalVideoWrap = styled.div<{ $mobileFs?: boolean }>`
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  border-radius: 12px;
  overflow: hidden;
  background: #0b0c0f;
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.5);

  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
  }

  @media (max-width: 768px) {
    border-radius: ${({ $mobileFs }) => $mobileFs ? '0' : '8px'};
    box-shadow: ${({ $mobileFs }) => $mobileFs ? 'none' : '0 20px 80px rgba(0, 0, 0, 0.5)'};

    ${({ $mobileFs }) => $mobileFs ? `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(90deg);
      width: 100vh;
      height: 100vw;
      padding-top: 0;

      video {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: 0;
      }
    ` : `
      video { border-radius: 8px; }
    `}
  }
`;

const DemoModalCloseBtn = styled.button`
  position: absolute;
  top: -44px;
  right: 0;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 4px 8px;
  transition: color 0.2s;
  font-family: 'Inter', sans-serif;

  &:hover { color: #fff; }

  @media (max-width: 768px) {
    top: -36px;
    font-size: 1.3rem;
  }
`;

const DEMO_VIDEO_TIME_KEY = 'demo_video_current_time';

const GlobalFooter: React.FC = () => {
  const [demoOpen, setDemoOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isMobileFullscreen, setIsMobileFullscreen] = useState(false);
  const demoVideoRef = useRef<HTMLVideoElement>(null);

  // Detect mobile viewport
  const isMobileRef = useRef(typeof window !== 'undefined' && window.matchMedia('(max-width: 820px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 820px)');
    const handler = (e: MediaQueryListEvent) => { isMobileRef.current = e.matches; };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const closeDemo = useCallback(() => {
    // Save current playback position before closing
    if (demoVideoRef.current && demoVideoRef.current.currentTime > 0) {
      try { sessionStorage.setItem(DEMO_VIDEO_TIME_KEY, String(demoVideoRef.current.currentTime)); } catch {}
    }
    setDemoOpen(false);
    if (demoVideoRef.current) demoVideoRef.current.pause();
    // Exit fullscreen if active
    if (document.fullscreenElement || (document as any).webkitFullscreenElement) {
      try { document.exitFullscreen().catch(() => {}); } catch {}
    }
    setIsMobileFullscreen(false);
    try {
      const orientation = (window.screen as any).orientation;
      if (orientation && orientation.unlock) orientation.unlock();
    } catch {}
  }, []);

  // Auto-play + resume position + mobile fullscreen when modal opens
  useEffect(() => {
    if (!demoOpen) return;
    const timer = setTimeout(() => {
      const video = demoVideoRef.current;
      if (!video) return;

      const restoreAndPlay = () => {
        // Pause ALL other video elements first (single-instance guarantee)
        document.querySelectorAll('video').forEach(v => {
          if (v !== video && !v.paused) v.pause();
        });

        try {
          const saved = sessionStorage.getItem(DEMO_VIDEO_TIME_KEY);
          if (saved) {
            const t = parseFloat(saved);
            if (t > 0 && isFinite(t) && t < (video.duration || Infinity)) {
              video.currentTime = t;
            }
          }
        } catch {}

        video.play().catch(() => {});
      };

      // Force the browser to start loading (hidden videos may not load automatically)
      if (video.readyState < 1) {
        video.load();
      }

      if (video.readyState >= 3) {
        restoreAndPlay();
      } else {
        // Listen for canplay which fires once enough data is buffered to start playback
        video.addEventListener('canplay', restoreAndPlay, { once: true });
        // Fallback: if canplay doesn't fire within 1s, try playing anyway
        setTimeout(() => {
          if (video.paused) {
            restoreAndPlay();
          }
        }, 1000);
      }

      // Mobile fullscreen
      if (isMobileRef.current) {
        (async () => {
          try {
            const fsTarget = video as any;
            const reqFS = fsTarget.requestFullscreen || fsTarget.webkitRequestFullscreen || fsTarget.webkitEnterFullScreen || fsTarget.mozRequestFullScreen;
            if (reqFS) {
              await reqFS.call(fsTarget);
              setIsMobileFullscreen(true);
              try {
                const orientation = (window.screen as any).orientation;
                if (orientation?.lock) await orientation.lock('landscape').catch(() => {});
              } catch {}
            }
          } catch {
            setIsMobileFullscreen(true);
          }
        })();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [demoOpen]);

  // Listen for fullscreen exit
  useEffect(() => {
    const handler = () => {
      if (!document.fullscreenElement && !(document as any).webkitFullscreenElement) {
        if (isMobileFullscreen) {
          setIsMobileFullscreen(false);
          try { const o = (window.screen as any).orientation; if (o?.unlock) o.unlock(); } catch {}
        }
        if (demoOpen && isMobileRef.current) closeDemo();
      }
    };
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
    };
  }, [isMobileFullscreen, demoOpen, closeDemo]);

  // Close on Escape key
  useEffect(() => {
    if (!demoOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDemo(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [demoOpen, closeDemo]);

  const handlePlaceholderClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    openConstructionNotice({ source: 'global_footer_placeholder_link' });
  };

  const handleDemoClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setDemoOpen(true);
  };

  const currentYear = new Date().getFullYear();

  return (
    <>
    <FooterWrapper>
      <FooterContent>
        <FooterColumn>
          <FooterLogo>
            <FooterLogoImage src="/images/project-olivia-logo.webp?v=20260715" alt="prest0 logo" />
            <FooterLogoText>prest<FooterInlineLogo src="/images/project-olivia-logo.webp?v=20260715" alt="0" /></FooterLogoText>
          </FooterLogo>
          <FooterDescription>24/7 <span className="ai-uppercase">AI</span> employees for law and finance</FooterDescription>
          <SocialLinks>
            <SocialIcon
              href="https://www.youtube.com/@Prest0nExecutiveAssistant"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />
                <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" />
              </svg>
            </SocialIcon>
            <SocialIcon
              href="https://x.com/milesprojolivia"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </SocialIcon>
            <SocialIcon
              href="https://www.linkedin.com/in/milesprojolivia/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                <rect x="2" y="9" width="4" height="12" />
                <circle cx="4" cy="4" r="2" />
              </svg>
            </SocialIcon>
            <SocialIcon as={Link} to="/insights" aria-label="insights">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="2" x2="12" y2="6" />
                <path d="M5 10a7 7 0 0 1 14 0c0 3.07-1.63 4.68-3 5.58a2 2 0 0 0-1 1.7V19a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-1.72a2 2 0 0 0-1-1.7C6.63 14.68 5 13.07 5 10z" />
                <line x1="9" y1="22" x2="15" y2="22" />
              </svg>
            </SocialIcon>
          </SocialLinks>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>platform</FooterTitle>
          <FooterLinks>
            <FooterLink>
              <Link
                to="/#features"
                onClick={() => {
                  // If already on home, manually scroll; otherwise Router will handle it
                  if (window.location.pathname === '/') {
                    setTimeout(() => {
                      const el = document.getElementById('features');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 50);
                  }
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 11 12 14 22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
                features
              </Link>
            </FooterLink>
            <FooterLink>
              <Link to="/security">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <circle cx="12" cy="11" r="2" />
                  <path d="M12 13v3" />
                </svg>
                security
              </Link>
            </FooterLink>
            <FooterLink>
              <a href="#" onClick={handlePlaceholderClick}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
                roadmap
              </a>
            </FooterLink>
          </FooterLinks>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>resources</FooterTitle>
          <FooterLinks>
            <FooterLink>
              <a href="#" onClick={(e) => { 
                e.preventDefault(); 
                window.open('https://brieflink.com/v/rgqru', 'InvestorBrief', 'width=1100,height=800,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes');
              }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v6a2 2 0 0 0 2 2h6" />
                  <path d="M5 14H4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2h-1" />
                  <path d="M5 14V5a2 2 0 0 1 2-2h7l5 5v6Z" />
                  <path d="M10 10v4" />
                  <path d="M14 10v4" />
                  <path d="M8 10v4" />
                </svg>
                for investors
              </a>
            </FooterLink>
            <FooterLink>
              <Link to="/team">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                team
              </Link>
            </FooterLink>
            <FooterLink>
              <Link to="/mission">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                  <line x1="4" y1="22" x2="4" y2="15" />
                </svg>
                mission
              </Link>
            </FooterLink>
            <FooterLink>
              <a href="#" onClick={handleDemoClick}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                demo
              </a>
            </FooterLink>
          </FooterLinks>
        </FooterColumn>
      </FooterContent>

      <FooterBottom>
        <Copyright>© {currentYear} Prest0 Inc. all rights reserved.</Copyright>
        <FooterBottomLinks>
          <Link to="/privacy-policy">privacy policy</Link>
          <Link to="/terms-of-service">terms of service</Link>
          <Link to="/cookie-policy">cookie policy</Link>
          <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer">
            sitemap
          </a>
        </FooterBottomLinks>
      </FooterBottom>
    </FooterWrapper>

    <DemoModalOverlay $isOpen={demoOpen} $mobileFs={isMobileFullscreen && isMobileRef.current} onClick={closeDemo}>
      <DemoModalContent $mobileFs={isMobileFullscreen && isMobileRef.current} onClick={(e) => e.stopPropagation()}>
        <DemoModalCloseBtn onClick={closeDemo}>✕</DemoModalCloseBtn>
        <DemoModalVideoWrap $mobileFs={isMobileFullscreen && isMobileRef.current}>
          <video
            ref={demoVideoRef}
            src="https://storage.googleapis.com/project-olivia-frontend-415/demo-video/prest0-demo-latest.mp4"
            controls
            playsInline
            /* preload="none": the open-effect calls video.load() when
               readyState < 1, so playback is unaffected — but the closed
               modal no longer fetches metadata of a 44 MB mp4 on every
               page that renders the footer (incl. the home page). */
            preload="none"
            onEnded={() => {
              closeDemo();
              setIsBookingModalOpen(true);
            }}
          />
        </DemoModalVideoWrap>
      </DemoModalContent>
    </DemoModalOverlay>
    <Modal
      isOpen={isBookingModalOpen}
      onClose={() => setIsBookingModalOpen(false)}
      title="book a live demo"
      size="xlarge"
      maxWidth="1100px"
      footer={null}
      noScroll={true}
    >
      <div style={{ height: 'calc(90vh - 80px)', minHeight: '600px', width: '100%', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
        <iframe
          src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ2bVpuAMuVJh3Xikmu1Bi9AuBaaHxB5a-HeOlE8xm3l2ehjgrdvG5QQEqmrLDKsF1Vgbncg2BIK?gv=true"
          style={{ border: 'none', width: '100%', height: '100%' }}
        ></iframe>
      </div>
    </Modal>
    </>
  );
};

export default GlobalFooter;
